/**
 * Reassign Positions to F&O Job Dialog
 *
 * Used from TWO places:
 * 1. Jobs tab (per-row action) — scoped to a source job's positions
 * 2. Positions tab (bulk action) — with pre-selected position IDs
 *
 * Lets the user pick a target F&O Job from any F&O Job (linked or unlinked),
 * preview the cascade fields, and confirm. Mirrors the existing
 * ReassignPositionsDialog pattern.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Loader2, CheckCircle, AlertTriangle, ArrowRight, X, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/hooks/useConfirm';
import {
  jobFamilySyncService,
  FoJobListItem,
  FoPositionListItem,
} from '@/services/api/organizationService';

interface ReassignPositionsToJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Mode A: Jobs tab — scope to a source job's positions
  sourceJobId?: string;
  sourceJobTitle?: string;
  // Mode B: Positions tab — pre-selected F&O PositionIds
  preSelectedPositions?: FoPositionListItem[];
  onComplete: () => void;
}

const ReassignPositionsToJobDialog: React.FC<ReassignPositionsToJobDialogProps> = ({
  open,
  onOpenChange,
  sourceJobId,
  sourceJobTitle,
  preSelectedPositions,
  onComplete,
}) => {
  const { confirm: showConfirm } = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState<FoJobListItem[]>([]);
  const [positions, setPositions] = useState<FoPositionListItem[]>([]);
  const [selectedPositionIds, setSelectedPositionIds] = useState<Set<string>>(new Set());

  // Target job selection
  const [targetSearch, setTargetSearch] = useState('');
  const [targetJobId, setTargetJobId] = useState<string | null>(null);

  // Cascade flags
  // Note: JobFamily is NOT cascaded — F&O's Position entity has no JobFamily
  // property. The family is inherited from the new JobId automatically.
  const [cascadeTitle, setCascadeTitle] = useState(true);
  const [cascadeDescription, setCascadeDescription] = useState(true);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ succeeded: number; failed: number; errors: Array<{ id: string; error: string }> } | null>(null);

  // ── Load jobs + positions on open ──────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    // Reset state
    setSelectedPositionIds(new Set());
    setTargetJobId(null);
    setTargetSearch('');
    setCascadeTitle(true);
    setCascadeDescription(true);
    setResult(null);

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        // Always load jobs for target picker
        const jobsPromise = jobFamilySyncService.listFoJobs();

        // Load positions for Jobs-tab mode (scoped to sourceJobId) OR use pre-selected
        if (sourceJobId) {
          const posPromise = jobFamilySyncService.listFoPositions();
          const [jobsData, posData] = await Promise.all([jobsPromise, posPromise]);
          if (cancelled) return;
          setJobs(jobsData.items);
          const sourcePositions = posData.items.filter(p => (p.job_id || '').trim() === sourceJobId);
          setPositions(sourcePositions);
          // Pre-select all positions from this source job
          setSelectedPositionIds(new Set(sourcePositions.map(p => p.position_id)));
        } else if (preSelectedPositions) {
          const jobsData = await jobsPromise;
          if (cancelled) return;
          setJobs(jobsData.items);
          setPositions(preSelectedPositions);
          setSelectedPositionIds(new Set(preSelectedPositions.map(p => p.position_id)));
        }
      } catch (e) {
        console.error('Failed to load data for reassign dialog:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, sourceJobId, preSelectedPositions]);

  // ── Filtered target jobs ────────────────────────────────────────────────

  const targetCandidates = useMemo(() => {
    return jobs
      .filter(j => !j.is_expired)                         // exclude retired
      .filter(j => j.job_id !== sourceJobId)              // exclude source
      .filter(j => {
        if (!targetSearch.trim()) return true;
        const s = targetSearch.trim().toLowerCase();
        return (
          j.job_id.toLowerCase().includes(s) ||
          (j.title_id || '').toLowerCase().includes(s) ||
          (j.description || '').toLowerCase().includes(s)
        );
      })
      .slice(0, 100); // cap to prevent huge lists
  }, [jobs, targetSearch, sourceJobId]);

  const targetJob = useMemo(
    () => jobs.find(j => j.job_id === targetJobId) || null,
    [jobs, targetJobId],
  );

  // ── Selection helpers ──────────────────────────────────────────────────

  const togglePosition = (pid: string) => {
    setSelectedPositionIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const allSelected = positions.length > 0 && positions.every(p => selectedPositionIds.has(p.position_id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedPositionIds(new Set());
    } else {
      setSelectedPositionIds(new Set(positions.map(p => p.position_id)));
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!targetJob || selectedPositionIds.size === 0) return;

    const confirmed = await showConfirm(
      `Reassign ${selectedPositionIds.size} position${selectedPositionIds.size === 1 ? '' : 's'} to "${targetJob.title_id || targetJob.job_id}"?\n\nThis will update JobId${cascadeTitle ? ', TitleId' : ''}${cascadeDescription ? ', Description' : ''} in F&O for each selected position. Job Family is inherited from the new Job automatically.`,
      'Confirm Reassignment',
      { confirmText: `Reassign ${selectedPositionIds.size}`, type: 'warning' },
    );
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const resp = await jobFamilySyncService.bulkReassignFoPositions({
        position_ids: Array.from(selectedPositionIds),
        target_job_id: targetJob.job_id,
        cascade_title: cascadeTitle,
        cascade_description: cascadeDescription,
      });
      setResult({
        succeeded: resp.succeeded,
        failed: resp.failed,
        errors: resp.results
          .filter(r => !r.success)
          .map(r => ({ id: r.position_id, error: r.error || 'Unknown error' })),
      });
      if (resp.failed === 0) {
        // Auto-close after brief delay on full success
        setTimeout(() => {
          onComplete();
        }, 1500);
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Reassignment failed';
      setResult({ succeeded: 0, failed: selectedPositionIds.size, errors: [{ id: 'general', error: detail }] });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!isSubmitting) onOpenChange(v); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        {/* Loading overlay — blocks all interaction while submitting */}
        {isSubmitting && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
            <Loader2 className="h-8 w-8 animate-spin text-heritage-blue mb-3" />
            <div className="text-sm font-medium text-gray-700">Reassigning positions in F&O...</div>
            <div className="text-xs text-gray-500 mt-1">This may take a minute. Please don't close this panel.</div>
          </div>
        )}
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ArrowRight className="h-5 w-5 text-heritage-blue" />
            Reassign Positions to Job
          </SheetTitle>
          {sourceJobId && (
            <SheetDescription>
              From: <span className="font-mono">{sourceJobId}</span> — {sourceJobTitle}
            </SheetDescription>
          )}
        </SheetHeader>

        {isLoading ? (
          <div className="py-12 text-center text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading F&O data...
          </div>
        ) : result ? (
          // Result panel
          <div className="py-6 space-y-4">
            <div className={cn(
              'p-4 rounded-lg',
              result.failed === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200',
            )}>
              <div className="flex items-center gap-2 mb-2">
                {result.failed === 0 ? (
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                )}
                <span className="font-semibold">
                  {result.failed === 0 ? 'Reassignment successful' : 'Reassignment partially failed'}
                </span>
              </div>
              <div className="text-sm text-gray-700">
                Succeeded: {result.succeeded}
                {result.failed > 0 && <span className="text-red-600"> · Failed: {result.failed}</span>}
              </div>
              {result.failed > 0 && (
                <div className="text-xs text-gray-500 mt-2">
                  Failed positions have been reconciled to match their actual F&O state.
                  Position counts in the Role Master will reflect the correct values.
                </div>
              )}
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-64 overflow-y-auto border rounded p-3 bg-gray-50 text-xs space-y-1">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-red-700">
                    <span className="font-mono">{e.id}</span>: {e.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="py-4 space-y-6">
            {/* Positions to reassign */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">
                  Positions ({selectedPositionIds.size} of {positions.length} selected)
                </h3>
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={toggleAll}>
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Button>
              </div>
              <div className="border rounded max-h-64 overflow-y-auto">
                {positions.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-400">No positions available.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead className="text-xs">Position</TableHead>
                        <TableHead className="text-xs">Worker</TableHead>
                        <TableHead className="text-xs">Current Title</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {positions.map(p => (
                        <TableRow
                          key={p.position_id}
                          className={cn(
                            'text-sm cursor-pointer hover:bg-gray-50',
                            selectedPositionIds.has(p.position_id) && 'bg-heritage-blue/5',
                          )}
                          onClick={() => togglePosition(p.position_id)}
                        >
                          <TableCell className="w-8">
                            <Checkbox checked={selectedPositionIds.has(p.position_id)} />
                          </TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap">{p.position_id}</TableCell>
                          <TableCell className="text-xs">
                            {p.worker_name || <span className="text-gray-400 italic">Vacant</span>}
                          </TableCell>
                          <TableCell className="text-xs">{p.title_id || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>

            {/* Target job picker */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Target F&O Job</h3>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search F&O jobs..."
                  value={targetSearch}
                  onChange={e => setTargetSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="border rounded max-h-48 overflow-y-auto">
                {targetCandidates.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-400">No matching jobs.</div>
                ) : (
                  targetCandidates.map(j => (
                    <button
                      key={j.job_id}
                      type="button"
                      onClick={() => setTargetJobId(j.job_id)}
                      className={cn(
                        'w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 text-sm',
                        targetJobId === j.job_id && 'bg-heritage-blue/5',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs text-gray-500 truncate">{j.job_id}</div>
                          <div className="text-sm text-gray-900 truncate">{j.title_id || '(no title)'}</div>
                          {j.job_family_id && (
                            <div className="text-xs text-gray-500">Family: {j.job_family_id}</div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <Badge variant="outline" className="text-[10px]">{j.positions_count} pos</Badge>
                          {j.linked_role_id && (
                            <div className="text-[10px] text-emerald-600 mt-1">Linked</div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Target preview + cascade flags */}
            {targetJob && (
              <div className="space-y-3">
                <div className="p-3 bg-heritage-blue/5 border border-heritage-blue/20 rounded-lg">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Will apply to all selected positions:</div>
                  <div className="grid grid-cols-[120px_1fr] gap-y-1 text-xs">
                    <span className="text-gray-500">New Job:</span>
                    <span className="font-mono">{targetJob.job_id}</span>
                    <span className="text-gray-500">New Title:</span>
                    <span>{targetJob.title_id || '—'}</span>
                    <span className="text-gray-500">Description:</span>
                    <span>{targetJob.description || '—'}</span>
                    <span className="text-gray-500">Job Family:</span>
                    <span>{targetJob.job_family_id || '—'}</span>
                    <span className="text-gray-500">Grade:</span>
                    <span>{targetJob.compensation_level || '—'}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-700">Cascade fields (uncheck to skip):</div>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={cascadeTitle} onCheckedChange={(v) => setCascadeTitle(!!v)} />
                    Update Position.Title (also cascades to Workers)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={cascadeDescription} onCheckedChange={(v) => setCascadeDescription(!!v)} />
                    Update Position.Description
                  </label>
                  <div className="text-[11px] text-gray-500 italic pl-6">
                    Job Family is inherited from the new Job — no separate field on Position to update.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <SheetFooter className="gap-2">
          {result ? (
            <Button onClick={() => onComplete()}>Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!targetJob || selectedPositionIds.size === 0 || isSubmitting}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Reassign {selectedPositionIds.size} Position{selectedPositionIds.size === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ReassignPositionsToJobDialog;
